import useSWRMutation from 'swr/mutation';

import { useGlobalConfig } from '../../../hooks/configContext';
import UtmLocalStorage from '../../../lib/analytics/queryParams/UtmLocalStorage';
import {
  convertPosthogCookieToObject,
  getPosthogCookieValueAsLabel,
} from '../../../lib/posthog/convertPosthogCookieUtil';
import {
  Cloud$Address,
  Cloud$BusinessUnitConfig,
} from '../../../types/cloudApi';
import { Cloud$MainHardwareType } from '../../../types/cloudEnums';
import { handleResponseErrors } from './ErrorHandling/handleResponseErrors';

type CreateInstantActivationArgs = {
  first_name: string;
  last_name: string;
  address: string;
  email: string;
  phone_number: string;
  consent: boolean;
  consent_text: string;
  monthlyEnergyBill?: number;
  locale: string;
  mainHardwareType?: Cloud$MainHardwareType;
};

type CreateInstantActivationUTMType = {
  utm_campaign?: string;
  utm_medium?: string;
  utm_source?: string;
  google_ads_click_id?: string;
};

type CreateInstantActivationRequest = {
  first_name: string;
  last_name: string;
  address: string;
  email: string;
  phone_number: string;
  business_unit: string;
  market: string;
  consent: boolean;
  consent_text: string;
  experiment_version?: string;
  utm_data: CreateInstantActivationUTMType;
  utm_data_initial: CreateInstantActivationUTMType;
  monthly_energy_bill?: number;
  preferred_locale: string;
  main_hardware_type?: Cloud$MainHardwareType;
};

type InstantActivationResponse = {
  success: boolean;
  interestId: string;
  address: Cloud$Address;
  message?: string;
};

const sendRequest = async (
  url: string,
  { arg }: { arg: CreateInstantActivationRequest },
) => {
  const response = await fetch(url, {
    method: 'post',
    body: JSON.stringify(arg),
    headers: { 'content-type': 'application/json' },
  });

  handleResponseErrors(response);

  const result = await response.json();
  if (!result.success) {
    throw new Error('Unexpected response from Cloud');
  }

  return result as InstantActivationResponse;
};

function resolveBUSlug(config: Cloud$BusinessUnitConfig) {
  if (
    config.matchedPartner &&
    config.matchedPartner.type === 'RESELLER_PARTNER'
  ) {
    return config.matchedPartner.config.slug;
  }

  return config.slug;
}

export default function useCreateInstantActivation() {
  const config = useGlobalConfig();

  const { trigger, isMutating } = useSWRMutation(
    `${process.env.NEXT_PUBLIC_OTOVOAPI_URL}/web/interests/instant-activation/`,
    sendRequest,
  );

  async function createInstantActivation({
    first_name,
    last_name,
    address,
    email,
    phone_number,
    consent,
    consent_text,
    monthlyEnergyBill,
    locale,
    mainHardwareType,
  }: CreateInstantActivationArgs): Promise<InstantActivationResponse> {
    const experimentVersion = getPosthogCookieValueAsLabel(
      convertPosthogCookieToObject(),
    );

    return trigger({
      first_name,
      last_name,
      address,
      email,
      phone_number,
      business_unit: resolveBUSlug(config.BU_CONFIG),
      market: config.BU_CONFIG.market,
      consent,
      consent_text,
      utm_data: UtmLocalStorage.retrieve(),
      utm_data_initial: UtmLocalStorage.retrieveInitial(),
      monthly_energy_bill: monthlyEnergyBill || undefined,
      experiment_version: experimentVersion || undefined,
      preferred_locale: locale,
      ...(mainHardwareType && { main_hardware_type: mainHardwareType }),
    });
  }

  return {
    createInstantActivation,
    isCreatingInstantActivation: isMutating,
  };
}
